import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectCurrentProject } from "../store/slices/projects";
import { openEventSource } from "../api/event-source";


const EVENT_CONFIG = [
  { eventName: "cohort.changed", handlerName: "onCohortChanged", label: "cohort" },
  { eventName: "tag.changed", handlerName: "onTagChanged", label: "tag" },
  { eventName: "category.changed", handlerName: "onCategoryChanged", label: "category" },
  { eventName: "fitfact.changed", handlerName: "onFitFactChanged", label: "fitfact" },
  { eventName: "article.changed", handlerName: "onArticleChanged", label: "article" },
  { eventName: "task.changed", handlerName: "onTaskChanged", label: "task" },
  { eventName: "scenario.changed", handlerName: "onScenarioChanged", label: "scenario" },
  { eventName: "onboarding.changed", handlerName: "onOnboardingChanged", label: "onboarding" },
  { eventName: "plan.changed", handlerName: "onPlanChanged", label: "plan" },
  { eventName: "rule.changed", handlerName: "onRuleChanged", label: "rule" },
  { eventName: "profile.changed", handlerName: "onProfileChanged", label: "profile" },
  { eventName: "project.changed", handlerName: "onProjectChanged", label: "project" },
  { eventName: "site.changed", handlerName: "onSiteChanged", label: "site" },
  { eventName: "lifepoint.changed", handlerName: "onLifePointChanged", label: "lifepoint" },
  { eventName: "legalDocument.changed", handlerName: "onLegalDocumentChanged", label: "legalDocument" },
  { eventName: "user.changed", handlerName: "onUserChanged", label: "user" },
  { eventName: "subject.changed", handlerName: "onSubjectChanged", label: "subject" },
  { eventName: "setting.changed", handlerName: "onSettingChanged", label: "setting" },
];

const connectionsByProject = new Map();

const getProjectConnection = (projectId) => {
  const key = String(projectId);
  const existing = connectionsByProject.get(key);

  if (existing) return existing;

  // LF360-1970: authorised by the session cookie, so the administrator's token
  // is no longer in the URL of a stream that stays open for the whole session.
  const eventSource = openEventSource("/api/v1/admin-events/stream", { projectId });
  const connection = {
    eventSource,
    subscribers: new Set(),
    listeners: [],
  };

  EVENT_CONFIG.forEach(({ eventName, handlerName, label }) => {
    const listener = (event) => {
      let payload;

      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        console.error(`Failed to parse ${label} event`, error);
        return;
      }

      connection.subscribers.forEach((subscriber) => {
        try {
          subscriber.current?.[handlerName]?.(payload);
        } catch (error) {
          console.error(`Failed to handle ${label} event`, error);
        }
      });
    };

    eventSource.addEventListener(eventName, listener);
    connection.listeners.push({ eventName, listener });
  });

  connectionsByProject.set(key, connection);
  return connection;
};

const subscribeToProjectEvents = (projectId, handlersRef) => {
  const key = String(projectId);
  const connection = getProjectConnection(projectId);

  connection.subscribers.add(handlersRef);

  return () => {
    connection.subscribers.delete(handlersRef);

    if (connection.subscribers.size > 0) return;

    connection.listeners.forEach(({ eventName, listener }) => {
      connection.eventSource.removeEventListener(eventName, listener);
    });
    connection.eventSource.close();
    connectionsByProject.delete(key);
  };
};

const useAdminEvents = ({
  onCohortChanged,
  onTagChanged,
  onCategoryChanged,
  onFitFactChanged,
  onArticleChanged,
  onTaskChanged,
  onScenarioChanged,
  onOnboardingChanged,
  onPlanChanged,
  onRuleChanged,
  onProfileChanged,
  onProjectChanged,
  onSiteChanged,
  onLifePointChanged,
  onLegalDocumentChanged,
  onUserChanged,
  onSubjectChanged,
  onSettingChanged,
} = {}) => {
  const currentProject = useSelector(selectCurrentProject);
  const projectId = currentProject?.id;
  const hasHandlers = Boolean(
    onCohortChanged ||
    onTagChanged ||
    onCategoryChanged ||
    onFitFactChanged ||
    onArticleChanged ||
    onTaskChanged ||
    onScenarioChanged ||
    onOnboardingChanged ||
    onPlanChanged ||
    onRuleChanged ||
    onProfileChanged ||
    onProjectChanged ||
    onSiteChanged ||
    onLifePointChanged ||
    onLegalDocumentChanged ||
    onUserChanged ||
    onSubjectChanged ||
    onSettingChanged
  );
  const handlersRef = useRef({
    onCohortChanged,
    onTagChanged,
    onCategoryChanged,
    onFitFactChanged,
    onArticleChanged,
    onTaskChanged,
    onScenarioChanged,
    onOnboardingChanged,
    onPlanChanged,
    onRuleChanged,
    onProfileChanged,
    onProjectChanged,
    onSiteChanged,
    onLifePointChanged,
    onLegalDocumentChanged,
    onUserChanged,
    onSubjectChanged,
    onSettingChanged,
  });

  useEffect(() => {
    handlersRef.current = {
      onCohortChanged,
      onTagChanged,
      onCategoryChanged,
      onFitFactChanged,
      onArticleChanged,
      onTaskChanged,
      onScenarioChanged,
      onOnboardingChanged,
      onPlanChanged,
      onRuleChanged,
      onProfileChanged,
      onProjectChanged,
      onSiteChanged,
      onLifePointChanged,
      onLegalDocumentChanged,
      onUserChanged,
      onSubjectChanged,
      onSettingChanged,
    };
  }, [
    onCohortChanged,
    onTagChanged,
    onCategoryChanged,
    onFitFactChanged,
    onArticleChanged,
    onTaskChanged,
    onScenarioChanged,
    onOnboardingChanged,
    onPlanChanged,
    onRuleChanged,
    onProfileChanged,
    onProjectChanged,
    onSiteChanged,
    onLifePointChanged,
    onLegalDocumentChanged,
    onUserChanged,
    onSubjectChanged,
    onSettingChanged,
  ]);

  useEffect(() => {
    if (!projectId || !hasHandlers) return undefined;

    return subscribeToProjectEvents(projectId, handlersRef);
  }, [projectId, hasHandlers]);
};

export default useAdminEvents;
